import type { EChartsOption } from 'echarts';
import { ChartPayload, ChartScale, ChartSeries, ChartType } from './chart-artifact.parse';
import { formatTruncated } from 'src/app/shared/number-format.util';

/**
 * Traduz o payload validado para uma opção do ECharts.
 *
 * Função pura e sem dependência do Angular, para as regras do contrato — pares X/Y, ordem das
 * categorias, resolução dos eixos, escala log, domínio — poderem ser testadas sem montar o
 * componente.
 *
 * Os ajustes de apresentação de 16/09/2026 (CHART_PRESENTATION_FRONTEND.md) moram aqui:
 * domínio colado nos dados, unidade sem duplicar, zoom/pan e legenda com rolagem.
 */

/** Paleta Vision, a mesma do datalog-chart, para o usuário reconhecer o canal pela cor. */
const PALETTE = [
  '#00FF00', '#FF8A00', '#9B51E0', '#F2C94C', '#2D9CDB',
  '#FFFFFF', '#7DBFFF', '#27AE60', '#C88CF0', '#FF0000',
];

const AXIS_TEXT = '#9b9b9b';
const AXIS_LINE = '#474747';
const GRID_LINE = '#252525';

/** Deslocamento de cada eixo extra do mesmo lado, em px. */
const AXIS_OFFSET = 64;

/** Margem visual do domínio, como fração do intervalo. */
const DOMAIN_PAD = 0.05;

/** Fator de cada clique de ampliar/reduzir. */
const ZOOM_STEP = 2;

/** Menor janela permitida, em % do domínio — abaixo disso os pontos viram um só. */
const MIN_ZOOM_SPAN = 1;

/**
 * IDs dos componentes de zoom. Há um para X e um para todos os Y: um único `inside` cobrindo
 * ambos guarda um só start/end, e um zoom por seleção só em X (modelo interno do toolbox) era
 * propagado para os Y pelo vínculo de eixo, fazendo o Y pular. Os botões do card despacham por
 * ID, não por posição, para não retargetar se alguém prefixar outro dataZoom.
 */
export const ZOOM_X_ID = 'chartZoomX';
export const ZOOM_Y_ID = 'chartZoomY';

/** Número finito ou o fallback: `getOption()` pode devolver undefined/NaN em estado transitório. */
export const finiteOr = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

/**
 * Próxima janela do dataZoom (em %, formato do ECharts) para um clique de ampliar/reduzir.
 *
 * Os botões existem porque o gesto (seleção, pinça, ctrl+roda) não é acessível por teclado
 * nem óbvio para quem não o conhece. Mantêm o centro, respeitam 0..100 e, encostando numa
 * borda, deslocam a janela em vez de cortá-la — o comportamento que o usuário espera de um
 * "+"/"−". Atuam só na visão: os dados recebidos não mudam.
 */
export function zoomWindow(
  current: { start: number; end: number },
  direction: 'in' | 'out',
): { start: number; end: number } {
  const start = Math.max(0, Math.min(100, current.start));
  const end = Math.max(start, Math.min(100, current.end));
  const center = (start + end) / 2;
  // Janela de largura zero (gesto extremo) precisa de um piso, senão reduzir devolve 0 para sempre.
  const span = Math.max(end - start, MIN_ZOOM_SPAN);
  const nextSpan = direction === 'in'
    ? Math.max(MIN_ZOOM_SPAN, span / ZOOM_STEP)
    : Math.min(100, span * ZOOM_STEP);

  let s = center - nextSpan / 2;
  let e = center + nextSpan / 2;
  if (s < 0) { e -= s; s = 0; }
  if (e > 100) { s -= e - 100; e = 100; }
  return { start: Math.max(0, s), end: Math.min(100, e) };
}

/**
 * Nome do eixo, sem repetir a unidade.
 *
 * O feedback trouxe um print com `RPM (RPM)`: quando o label já É a unidade, concatenar produz
 * ruído. A comparação ignora caixa porque `rpm` e `RPM` são a mesma coisa para quem lê.
 */
export function axisName(label: string, unit: string | null): string {
  if (!unit) return label;
  if (label.trim().toLowerCase() === unit.trim().toLowerCase()) return label;
  return `${label} (${unit})`;
}

/**
 * Domínio de um eixo numérico, colado nos dados.
 *
 * O padrão do ECharts para eixo `value` inclui o zero, e foi isso que empurrou uma temperatura
 * de 91,76 a 109,83 °C para o canto de um eixo que começava em zero. Aqui o intervalo sai dos
 * próprios valores com uma margem pequena — sem inserir zero artificial e sem esconder um zero
 * ou outlier que exista de verdade.
 *
 * Em escala log a margem é calculada no espaço logarítmico: 5% do intervalo linear seria
 * imperceptível num extremo e gigante no outro.
 *
 * Valores todos iguais dariam intervalo zero, que o ECharts desenha como um eixo degenerado;
 * nesse caso a margem vem do próprio valor (ou de 1, quando ele é zero).
 */
export function axisDomain(
  values: (number | null)[],
  scale: ChartScale,
): { min: number; max: number } {
  const finite = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (!finite.length) return { min: 0, max: 1 };

  if (scale === 'log10') {
    const positive = finite.filter((v) => v > 0);
    if (!positive.length) return { min: 1, max: 10 };
    const lo = Math.log10(Math.min(...positive));
    const hi = Math.log10(Math.max(...positive));
    const pad = (hi - lo) * DOMAIN_PAD || 0.1;
    return { min: 10 ** (lo - pad), max: 10 ** (hi + pad) };
  }

  const lo = Math.min(...finite);
  const hi = Math.max(...finite);
  const pad = (hi - lo) * DOMAIN_PAD || Math.abs(lo) * DOMAIN_PAD || 1;
  return { min: lo - pad, max: hi + pad };
}

/**
 * Se cabe a nota "Visualização resumida para facilitar a leitura.".
 *
 * Só com `source_points` maior que `points`, que é a única evidência de redução que o payload
 * carrega. Sem o campo, a redução é desconhecida — e o contrato manda omitir a nota na dúvida,
 * em vez de afirmar uma amostragem que pode não ter acontecido.
 */
export function shouldShowReducedNote(
  chart: Pick<ChartPayload, 'points' | 'source_points'>,
): boolean {
  const source = chart.source_points;
  return typeof source === 'number' && typeof chart.points === 'number' && source > chart.points;
}

/**
 * Ordem global das categorias: primeira ocorrência, percorrendo as séries e, dentro de cada
 * uma, os X na ordem em que vieram. Ordenar alfabeticamente inventaria uma ordem que o backend
 * não pediu, e uma categoria ausente numa série não é zero — apenas não tem barra ali.
 */
export function chartCategories(series: Pick<ChartSeries, 'x'>[]): string[] {
  const order: string[] = [];
  const seen = new Set<string>();
  for (const s of series) {
    for (const x of s.x as string[]) {
      if (!seen.has(x)) { seen.add(x); order.push(x); }
    }
  }
  return order;
}

/** Duração em segundos → `m:ss` (segundos truncados, nunca arredondados). X temporal é duração relativa ao log, nunca data. */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.trunc(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Rótulo de tick numérico: até 3 casas decimais (cortadas, nunca arredondadas), sem zeros à direita.
 *
 * Com o domínio colado nos dados, o ECharts usa o mínimo/máximo exatos como ticks das pontas — e
 * um limite como `lo - 5%` vira `0.3257848620414734` na tela. Três casas cobrem bar, lambda e
 * log10 sem virar ruído; inteiros continuam inteiros.
 */
export function formatTick(value: number): string {
  return formatTruncated(value, { locale: 'en-US', useGrouping: false });
}

/** `area` não existe no ECharts: é uma linha com preenchimento — e nunca empilhada. */
const echartsType = (type: ChartType): 'line' | 'scatter' | 'bar' =>
  type === 'area' ? 'line' : type;

/**
 * Barra e área medem grandeza a partir do zero: a altura do desenho É o valor, então cortar a
 * base mentiria sobre a proporção. Dispersão e linha medem posição, e aí enquadrar nos dados é
 * o que torna o gráfico legível.
 */
const needsZeroBaseline = (type: ChartType) => type === 'bar' || type === 'area';

export function buildChartOption(chart: ChartPayload, panelHeight = PANEL_HEIGHT): EChartsOption {
  // Eixos Y demais para caber num grid só: cada série vira uma faixa. Ver o
  // bloco PAINÉIS EMPILHADOS no fim do arquivo.
  if (isStackedChart(chart)) return stackedChartOption(chart, panelHeight);

  const categorical = chart.x_axis.kind === 'category';
  const categories = categorical ? chartCategories(chart.series) : [];
  const effectiveType = (s: ChartSeries) => (s.type ?? chart.chart_type) as ChartType;
  const anyZeroBaseline = chart.series.some((s) => needsZeroBaseline(effectiveType(s)));

  // Um eixo pode empurrar o seguinte do mesmo lado; o primeiro de cada lado fica colado.
  const usedPerSide: Record<'left' | 'right', number> = { left: 0, right: 0 };
  const yAxis = chart.y_axes.map((axis) => {
    const offset = usedPerSide[axis.side] * AXIS_OFFSET;
    usedPerSide[axis.side] += 1;

    // Domínio por eixo: só as séries ligadas a ele entram na conta.
    const values = chart.series
      .filter((s) => s.y_axis === axis.id)
      .flatMap((s) => s.y);
    const domain = anyZeroBaseline ? null : axisDomain(values, axis.scale);

    return {
      type: axis.scale === 'log10' ? ('log' as const) : ('value' as const),
      name: axisName(axis.label, axis.unit),
      // `middle` + rotação mantém o título dentro da área reservada; com o padrão (`end`) ele
      // fica no canto e é o primeiro a ser cortado, que foi o corte relatado no feedback.
      nameLocation: 'middle' as const,
      nameGap: 46,
      nameRotate: 90,
      nameTextStyle: { color: AXIS_TEXT },
      position: axis.side,
      offset,
      ...(domain ? { min: domain.min, max: domain.max } : {}),
      axisLine: { show: true, lineStyle: { color: AXIS_LINE } },
      axisLabel: { color: AXIS_TEXT, hideOverlap: true, formatter: (v: number) => formatTick(v) },
      splitLine: { lineStyle: { color: GRID_LINE } },
    };
  });

  const axisIndexById = new Map(chart.y_axes.map((a, i) => [a.id, i]));

  const series = chart.series.map((s, i) => {
    const type = effectiveType(s);
    const color = PALETTE[i % PALETTE.length];

    // Em categoria o ECharts posiciona pelo eixo, então vai só o valor; nos demais o par
    // [x, y] mantém cada ponto na sua coordenada — inclusive X repetido e fora de ordem.
    const data = categorical
      ? (s.x as string[]).map((x, j) => [x, s.y[j]])
      : (s.x as number[]).map((x, j) => [x, s.y[j]]);

    return {
      name: s.name,
      type: echartsType(type),
      yAxisIndex: axisIndexById.get(s.y_axis) ?? 0,
      data,
      // `null` é lacuna declarada: conectar os dois lados desenharia dado inexistente.
      connectNulls: false,
      symbolSize: type === 'scatter' ? 6 : 0,
      showSymbol: type === 'scatter',
      lineStyle: { color, width: 1.6 },
      itemStyle: { color },
      ...(type === 'area' ? { areaStyle: { color, opacity: 0.15 } } : {}),
    };
  });

  const xName = axisName(chart.x_axis.label, chart.x_axis.unit);
  const xDomain = categorical || anyZeroBaseline
    ? null
    : axisDomain(chart.series.flatMap((s) => s.x as number[]), chart.x_axis.scale);

  const xAxis = categorical
    ? {
        type: 'category' as const,
        data: categories,
        name: xName,
        nameLocation: 'middle' as const,
        nameGap: 32,
        nameTextStyle: { color: AXIS_TEXT },
        axisLine: { lineStyle: { color: AXIS_LINE } },
        axisLabel: { color: AXIS_TEXT, hideOverlap: true },
      }
    : {
        type: chart.x_axis.scale === 'log10' ? ('log' as const) : ('value' as const),
        name: xName,
        nameLocation: 'middle' as const,
        nameGap: 32,
        nameTextStyle: { color: AXIS_TEXT },
        ...(xDomain ? { min: xDomain.min, max: xDomain.max } : {}),
        axisLine: { lineStyle: { color: AXIS_LINE } },
        splitLine: { lineStyle: { color: GRID_LINE } },
        axisLabel: {
          color: AXIS_TEXT,
          hideOverlap: true,
          formatter: chart.x_axis.kind === 'time'
            ? (v: number) => formatDuration(v)
            : (v: number) => formatTick(v),
        },
      };

  return {
    backgroundColor: 'transparent',
    animation: false,
    // `containLabel` reserva os rótulos; o resto da folga é para os títulos dos eixos e para o
    // primeiro/último tick de X não encostarem na borda do card.
    grid: {
      left: 8,
      right: 8 + Math.max(0, usedPerSide.right - 1) * AXIS_OFFSET,
      top: chart.series.length > 1 ? 40 : 20,
      bottom: 48,
      containLabel: true,
    },
    tooltip: { trigger: categorical ? 'axis' : 'item', confine: true },
    legend: chart.series.length > 1
      ? {
          // Rolagem própria: com cinco séries de nomes longos a legenda envolvia o gráfico.
          type: 'scroll' as const,
          data: chart.series.map((s) => s.name),
          textStyle: { color: AXIS_TEXT },
          top: 0,
        }
      : undefined,
    // Zoom por seleção/pinça e pan arrastando. `zoomOnMouseWheel: 'ctrl'` é o que impede o
    // gráfico de sequestrar a rolagem da conversa quando o ponteiro passa por cima dele.
    dataZoom: [
      {
        id: ZOOM_X_ID,
        type: 'inside' as const,
        xAxisIndex: 0,
        zoomOnMouseWheel: 'ctrl' as const,
        moveOnMouseMove: true,
        moveOnMouseWheel: false,
        filterMode: 'none' as const,
      },
      {
        id: ZOOM_Y_ID,
        type: 'inside' as const,
        yAxisIndex: chart.y_axes.map((_, i) => i),
        zoomOnMouseWheel: 'ctrl' as const,
        moveOnMouseMove: true,
        moveOnMouseWheel: false,
        filterMode: 'none' as const,
      },
    ],
    toolbox: {
      right: 8,
      top: 0,
      itemSize: 13,
      iconStyle: { borderColor: AXIS_TEXT },
      emphasis: { iconStyle: { borderColor: '#d5d5d5' } },
      feature: {
        // Seleção de região em X e Y, sem filtrar pontos (o inside também usa 'none'); cada eixo
        // fica vinculado ao seu próprio componente declarado acima.
        dataZoom: {
          xAxisIndex: 0,
          yAxisIndex: chart.y_axes.map((_, i) => i),
          filterMode: 'none' as const,
          title: { zoom: '', back: '' },
        },
      },
    },
    xAxis,
    yAxis,
    series,
  } as EChartsOption;
}

/* ══════════════════════════════════════════════════════════════════════
   PAINÉIS EMPILHADOS
   ══════════════════════════════════════════════════════════════════════

   Com três ou mais eixos Y sobrepostos num único grid, as escalas comem a área
   do plot e as linhas passam a se cruzar sem relação nenhuma entre si. A leitura
   de datalogger é por canal, não por sobreposição: aqui cada série ganha a
   própria faixa com o próprio eixo Y, todas dividem o X (tempo), o cursor é
   ligado entre elas e há um só slider no rodapé.

   Mesmo desenho do FT Pilot desktop (AutoSync, `src/lib/stackedOptions.ts`),
   para o mesmo artifact ser lido do mesmo jeito nos dois lugares. */

/** A partir de quantos eixos Y a sobreposição deixa de se ler. */
export const STACK_FROM = 3;

const STACK_TOP = 26;       // acima da primeira faixa: cabe o título dela
const STACK_PANEL_GAP = 26; // espaço para o título da faixa seguinte
const STACK_X_AXIS_H = 28;  // rótulos do X
const STACK_X_NAME_H = 18;  // nome do eixo X
const STACK_SLIDER_H = 22;
const STACK_BOTTOM = 14;
const STACK_LEFT = 68;
const STACK_RIGHT = 16;

export const PANEL_HEIGHT = 92;
/** Faixa mais baixa onde a coluna do chat é estreita (celular, janela pequena). */
export const PANEL_HEIGHT_COMPACT = 74;

/**
 * Empilha quando há eixos Y demais para sobrepor e o X é contínuo.
 *
 * Categoria fica de fora porque barra por categoria já se lê num grid só, e
 * empilhar uma barra por faixa perderia a comparação entre elas — que é
 * justamente para o que esse gráfico serve.
 */
export function isStackedChart(chart: ChartPayload): boolean {
  if (chart.x_axis.kind === 'category') return false;
  if (chart.y_axes.length < STACK_FROM) return false;
  return chart.series.every((s) => {
    const type = (s.type ?? chart.chart_type) as ChartType;
    return type === 'line' || type === 'area' || type === 'scatter';
  });
}

/**
 * Altura total do canvas para `count` faixas.
 *
 * Precisa ser calculada: o CSS não sabe quantas séries vieram, e uma altura
 * fixa ou espremeria oito faixas em 320px ou deixaria vão morto com três.
 */
export function stackedChartHeight(count: number, panelHeight = PANEL_HEIGHT): number {
  return STACK_TOP
    + count * panelHeight
    + Math.max(0, count - 1) * STACK_PANEL_GAP
    + STACK_X_AXIS_H
    + STACK_X_NAME_H
    + STACK_SLIDER_H
    + STACK_BOTTOM;
}

/** Título da faixa, sem repetir a unidade (`RPM  RPM`). */
function panelTitle(name: string, unit: string | null): string {
  if (!unit) return name;
  return name.trim().toLowerCase() === unit.trim().toLowerCase() ? name : `${name}  ${unit}`;
}

function stackedChartOption(chart: ChartPayload, panelHeight: number): EChartsOption {
  const last = chart.series.length - 1;
  const all = chart.series.map((_, i) => i);
  const unitById = new Map(chart.y_axes.map((a) => [a.id, a.unit]));
  const xFormatter = chart.x_axis.kind === 'time'
    ? (v: number) => formatDuration(v)
    : (v: number) => formatTick(v);

  return {
    backgroundColor: 'transparent',
    animation: false,
    textStyle: { color: AXIS_TEXT },

    // Um grid por faixa, empilhados de cima para baixo.
    grid: chart.series.map((_, i) => ({
      left: STACK_LEFT,
      right: STACK_RIGHT,
      top: STACK_TOP + i * (panelHeight + STACK_PANEL_GAP),
      height: panelHeight,
    })),

    // Só a última faixa desenha o eixo X: ele é o mesmo para todas, e repetir os
    // rótulos entre as faixas gastaria a altura que é do próprio dado.
    xAxis: chart.series.map((_, i) => ({
      type: chart.x_axis.scale === 'log10' ? ('log' as const) : ('value' as const),
      gridIndex: i,
      axisLine: { show: i === last, lineStyle: { color: AXIS_LINE } },
      axisTick: { show: false },
      axisLabel: { show: i === last, color: AXIS_TEXT, hideOverlap: true, formatter: xFormatter },
      ...(i === last
        ? {
            name: axisName(chart.x_axis.label, chart.x_axis.unit),
            nameLocation: 'middle' as const,
            nameGap: 22,
            nameTextStyle: { color: AXIS_TEXT },
          }
        : {}),
      splitLine: { lineStyle: { color: GRID_LINE } },
    })),

    // O nome do eixo faz as vezes de título da faixa: vai para o canto superior
    // esquerdo, na cor da série. É o que dispensa a legenda.
    yAxis: chart.series.map((s, i) => {
      const axis = chart.y_axes.find((a) => a.id === s.y_axis);
      return {
        type: axis?.scale === 'log10' ? ('log' as const) : ('value' as const),
        gridIndex: i,
        scale: true,
        splitNumber: 3,
        name: panelTitle(s.name, unitById.get(s.y_axis) ?? null),
        nameLocation: 'end' as const,
        nameGap: 8,
        nameTextStyle: {
          color: PALETTE[i % PALETTE.length],
          align: 'left' as const,
          padding: [0, 0, 0, -STACK_LEFT + 8],
        },
        axisLine: { show: true, lineStyle: { color: AXIS_LINE } },
        axisTick: { show: false },
        axisLabel: { color: AXIS_TEXT, hideOverlap: true, formatter: (v: number) => formatTick(v) },
        splitLine: { lineStyle: { color: GRID_LINE } },
      };
    }),

    series: chart.series.map((s, i) => {
      const type = (s.type ?? chart.chart_type) as ChartType;
      const color = PALETTE[i % PALETTE.length];
      return {
        name: s.name,
        type: echartsType(type),
        xAxisIndex: i,
        yAxisIndex: i,
        data: (s.x as number[]).map((x, j) => [x, s.y[j]]),
        connectNulls: false,
        showSymbol: type === 'scatter',
        symbolSize: type === 'scatter' ? 6 : 0,
        lineStyle: { color, width: 1.4 },
        itemStyle: { color },
        ...(type === 'area' ? { areaStyle: { color, opacity: 0.15 } } : {}),
      };
    }),

    // Cursor único atravessando as faixas: é o que permite ler o mesmo instante
    // em todos os canais, que é a razão de empilhar.
    axisPointer: { link: [{ xAxisIndex: 'all' }], lineStyle: { color: '#6a6d75' } },
    tooltip: {
      trigger: 'axis' as const,
      // richText, não HTML: o nome da série vem do payload e nunca deve virar markup.
      renderMode: 'richText' as const,
      confine: true,
      valueFormatter: (v: unknown) =>
        typeof v === 'number' ? formatTick(v) : v == null ? '—' : String(v),
      backgroundColor: 'rgba(18, 18, 18, 0.92)',
      borderColor: AXIS_TEXT,
      textStyle: { color: '#fff' },
      axisPointer: {
        type: 'line' as const,
        label: { formatter: (p: { value: unknown }) => xFormatter(Number(p.value)) },
      },
    },

    dataZoom: [
      {
        id: ZOOM_X_ID,
        type: 'inside' as const,
        xAxisIndex: all,
        filterMode: 'none' as const,
        zoomOnMouseWheel: 'ctrl' as const,
        moveOnMouseMove: true,
        moveOnMouseWheel: false,
      },
      // Um slider só, no rodapé, comandando todas as faixas. Não leva ID: os
      // botões do card despacham no `inside`, e o slider acompanha pelo eixo.
      {
        type: 'slider' as const,
        xAxisIndex: all,
        filterMode: 'none' as const,
        height: STACK_SLIDER_H,
        bottom: STACK_BOTTOM - 4,
        left: STACK_LEFT,
        right: STACK_RIGHT,
        backgroundColor: '#0b0c0e',
        borderColor: '#2a2c31',
        fillerColor: 'rgba(214, 50, 31, 0.08)',
        dataBackground: {
          lineStyle: { color: '#4a4d55', width: 1 },
          areaStyle: { color: 'rgba(74, 77, 85, 0.2)' },
        },
        selectedDataBackground: {
          lineStyle: { color: '#b8887f', width: 1 },
          areaStyle: { color: 'rgba(214, 50, 31, 0.12)' },
        },
        handleStyle: { color: '#d6321f', borderColor: '#d6321f' },
        moveHandleStyle: { color: '#d6321f' },
        brushSelect: false,
        textStyle: { color: AXIS_TEXT },
        labelFormatter: (v: number) => xFormatter(v),
      },
    ],
  } as EChartsOption;
}
